import { MONTH_REGEX } from './date.js';
// ─── Shared regex patterns ────────────────────────────────────────────────
/** Simple email pattern used by both backend Zod schemas and frontend form validators. */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Bare domain pattern (no scheme, no "@"). Accepts e.g. "acme.com", "sub.acme.io". */
export const DOMAIN_REGEX = /^[a-z0-9][a-z0-9-]*(\.[a-z0-9-]+)+$/;
// ─── Generic validators ───────────────────────────────────────────────────
/** Returns error message if value is empty after trim, null otherwise. */
export function validateRequired(value, fieldLabel) {
    return value.trim() ? null : `${fieldLabel} is required`;
}
/** Returns error message if value is not a valid email, null otherwise. */
export function validateEmail(value) {
    if (!value.trim())
        return 'Email is required';
    if (!EMAIL_REGEX.test(value.trim()))
        return 'Please enter a valid email';
    return null;
}
/** Returns error message if value is not a valid bare domain (e.g. "acme.com"). */
export function validateDomain(value) {
    if (!value.trim())
        return 'Domain is required';
    if (!DOMAIN_REGEX.test(value.trim().toLowerCase())) {
        return 'Please enter a valid domain (e.g. acme.com)';
    }
    return null;
}
/** Returns error message if value is present but not a valid HTTPS URL. */
export function validateHttpsUrl(value) {
    if (!value)
        return null;
    try {
        const url = new URL(value);
        if (url.protocol !== 'https:')
            return 'Must start with https://';
        if (!url.hostname.includes('.'))
            return 'Please enter a valid URL';
        return null;
    }
    catch {
        return 'Please enter a valid URL (e.g. https://example.com)';
    }
}
// ─── Domain-specific validators ───────────────────────────────────────────
const LINKEDIN_PROFILE_BASE = 'https://www.linkedin.com/in/';
/**
 * What LinkedIn allows in the vanity part of a profile URL: letters (including
 * accented ones — vanity URLs are localised), digits, hyphens, underscores and
 * percent escapes. Notably no dots, which is what keeps a pasted domain from
 * being read as a handle.
 */
const LINKEDIN_HANDLE = /^[\p{L}\p{N}\-_%]+$/u;
/**
 * Pull the handle out of anything a founder is likely to paste or type.
 *
 * Copying the URL out of the address bar is the common case, but people also
 * type their own handle from memory — and being told "must be
 * https://linkedin.com/in/username" when you typed your username is a silly
 * thing for a form to do when it can plainly see what you meant.
 *
 * Accepts the bare handle, any protocol/subdomain/trailing-slash variation of
 * the profile URL, and query strings LinkedIn appends when you share a profile.
 * Returns null when it cannot find a handle it is confident about, which is
 * what makes this safe to use for both validation and normalisation.
 */
export function linkedInHandle(value) {
    const trimmed = value.trim();
    if (!trimmed)
        return null;
    // Everything up to the vanity segment, whichever way it was written, plus the
    // query string and any trailing slash LinkedIn adds.
    const handle = trimmed
        .replace(/^https?:\/\//i, '')
        .replace(/^([a-z]{2,3}\.)?linkedin\.com\//i, '')
        .replace(/^in\//i, '')
        .replace(/[?#].*$/, '')
        .replace(/\/+$/, '');
    return LINKEDIN_HANDLE.test(handle) ? handle : null;
}
/**
 * The canonical profile URL for a handle or URL, or null when there is no
 * handle to be found. Stored form is always the same shape, whatever was typed.
 */
export function normalizeLinkedInUrl(value) {
    const handle = linkedInHandle(value);
    return handle ? `${LINKEDIN_PROFILE_BASE}${handle}` : null;
}
/** Returns error message if value is present but holds no LinkedIn handle. */
export function validateLinkedInUrl(value) {
    if (!value.trim())
        return null;
    return linkedInHandle(value) ? null : 'Enter your LinkedIn username, or paste your profile URL';
}
/** Returns error message if value is not a positive number (strips $, commas, whitespace). */
export function validatePositiveAmount(value) {
    if (!value.trim())
        return null;
    const num = Number(value.replace(/[$,\s]/g, ''));
    if (Number.isNaN(num) || num <= 0)
        return 'Must be a number greater than 0';
    return null;
}
/** Returns error message if value is present but not a valid `YYYY-MM` month. */
export function validateMonth(value) {
    if (!value.trim())
        return null;
    if (!MONTH_REGEX.test(value.trim()))
        return 'Please enter a valid month';
    return null;
}
//# sourceMappingURL=validation.js.map